import { Box, ButtonBase, Container, styled } from '@mui/material';
import clsx from 'clsx';
import { useState } from 'react';

import { routes } from '@/assets/constants/routes-no.ts';
import { CompetitionWinnersDialog } from '@/components/CompetitionWinnersDialog/index.ts';
import { LoanExampleText } from '@/components/LoanExampleText/index.ts';
import { sendUserPortalEvent } from '@/lib/analytics/unified.ts';
import type { CompetitionWinner, CompetitionWithConfig } from '@/lib/api/requests.ts';
import { NumberCircle } from '@/ui/NumberCircle.tsx';
import { NumberedList, NumberedListItem } from '@/ui/NumberedList.tsx';

import Beach from '../_assets/beach.svg?react';
import Cloud from '../_assets/cloud.svg?react';
import MembersOnly from '../_assets/members-only.svg?react';
import Palm from '../_assets/palm.svg?react';
import Sailboat from '../_assets/sailboat.svg?react';
import styles from '../_assets/styles.module.css';
import Sun from '../_assets/sun.svg?react';
import Umbrella from '../_assets/umbrella.svg?react';
import { CTA } from '../_components/CTA.tsx';
import { useHasEnded } from '../_components/useHasEnded.ts';

const Root = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  width: '100%',
  isolation: 'isolate',

  '--height': '860px',
  [theme.breakpoints.up('sm')]: {
    '--height': '980px',
  },
  [theme.breakpoints.up('lg')]: {
    '--height': '950px',
  },
  // [theme.breakpoints.up('xl')]: {
  //   '--height': '1000px',
  // },

  height: 'var(--height)',
}));

export const TopSection = (props: {
  activeCompetition: CompetitionWithConfig | undefined;
  winners: CompetitionWinner[] | undefined;
}) => {
  return (
    <>
      <Root>
        <Background />

        <Container maxWidth="xl" sx={{ pt: { xs: 2, md: 3 } }}>
          <Content activeCompetition={props.activeCompetition} winners={props.winners} />
        </Container>
      </Root>

      <Disclaimer />
    </>
  );
};

const Background = () => (
  <div className="absolute inset-0 isolate -z-10 flex flex-col overflow-hidden bg-linear-to-b from-[#D1ECFE] to-[#E8F6FF] to-50%">
    <Sun className="absolute top-24 right-20 -z-10 hidden scale-50 sm:block md:bottom-80 md:scale-75 xl:right-3/12 xl:bottom-92 xl:scale-100 2xl:left-1/2 2xl:translate-x-72" />
    <Beach
      className={clsx(styles.beach, 'mt-auto -mb-24 w-[500%] sm:w-[300%] lg:w-[150%] xl:w-full')}
      preserveAspectRatio="none"
    />
    <Cloud className="absolute top-24 -left-40 xl:left-1/3" />
    <Cloud className="absolute top-48 right-4 scale-75 xl:top-6 xl:right-1/12 xl:scale-100" />
    <Cloud className="absolute right-28 bottom-92 hidden scale-33 sm:block md:right-36 md:bottom-96 xl:right-[30%] xl:bottom-104 xl:scale-50 2xl:left-1/2 2xl:translate-x-48" />
    <Sailboat
      className={clsx(
        styles.sailboat,
        'absolute right-28 bottom-64 hidden scale-50 sm:block md:right-36 lg:bottom-68 xl:right-[30%] xl:scale-100 2xl:left-1/2 2xl:translate-x-48',
      )}
    />
    <Umbrella className="absolute bottom-0 -left-20 scale-75 md:bottom-8 md:left-64 md:scale-100 xl:bottom-16 xl:scale-125 2xl:right-0 2xl:left-1/2 2xl:-translate-x-100" />
    <Palm className="absolute -right-36 bottom-24 hidden scale-[110%] xl:block 2xl:left-1/2 2xl:translate-x-96" />
  </div>
);

const Disclaimer = () => (
  <div className="bg-white pt-4">
    <Container maxWidth="md">
      <div className="text-center typography-v1-body4 text-balance">
        <LoanExampleText />
      </div>
    </Container>
  </div>
);

const Content = (props: {
  activeCompetition: CompetitionWithConfig | undefined;
  winners: CompetitionWinner[] | undefined;
}) => {
  const ended = useHasEnded();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Box
      sx={{
        mx: { xs: 'auto', sm: 0 },
      }}
    >
      <MembersOnly className="mb-2 h-10 w-auto md:h-16 lg:h-auto" />
      <h1 className="mb-4 text-2xl leading-tight font-bold text-balance sm:text-5xl xl:text-6xl 2xl:text-5xl">
        Vinn en ferie verdt 50 000 kr
        <br />– og få ett års pause fra gjelden din
      </h1>
      <p className="mb-6 max-w-92.5 typography-v1-h4 font-normal sm:max-w-150 md:mb-8 md:max-w-187.5 lg:mb-4 xl:max-w-200">
        Vi vet det kan være vanskelig å kombinere sommerferie med forbruksgjeld. Derfor skal vi
        overta gjelden til én heldig vinner i ett år – og sende personen på ferie til en verdi av 50
        000 kr. Sjekk om du kan få lavere rente for å delta i trekningen.
        <br />
        <b>
          <i>Ikke lån mer, fiks gjelden du allerede har!</i>
        </b>
      </p>
      <p className="mb-2 typography-v1-h4">For å delta:</p>

      <NumberedList
        sx={{ mb: 2 }}
        renderNumber={(props) => (
          <NumberCircle
            sx={{
              fontSize: 14,
              height: 20,
              backgroundColor: '#FBFDFF',
              color: '#031839',
            }}
            {...props}
          />
        )}
        renderLabel={(props) => <p {...props} className="typography-v1-body3" />}
      >
        <NumberedListItem>Logg inn med BankID</NumberedListItem>
        <NumberedListItem>Sjekk om du kan få lavere rente på din gjeld</NumberedListItem>
        <NumberedListItem>Delta i trekningen</NumberedListItem>
      </NumberedList>

      {ended && (
        <p className="mb-6 typography-v1-h4">
          <span>Denne trekningen er avsluttet. </span>
          <ButtonBase
            onClick={() => setIsOpen(true)}
            sx={{
              color: '#74C6FD',
              textDecoration: 'underline',
              mb: 0.5,
              fontWeight: 700,
              ml: 1,
            }}
          >
            Se vinneren her
          </ButtonBase>
        </p>
      )}

      <Box sx={{ mb: 2 }}>
        <a
          href={routes.MAY_MEMBERS_2025_COMPETITION_TERMS}
          style={{ color: 'inherit' }}
          className="underline"
          target="_blank"
        >
          <span className="typography-v1-body3">Vilkår og betingelser for deltagelse</span>
        </a>
      </Box>

      <CTA
        activeCompetition={props.activeCompetition}
        onClick={() => {
          sendUserPortalEvent('maym25_competition_cta_top_clicked');
        }}
      />

      {props.winners && (
        <CompetitionWinnersDialog
          open={isOpen}
          onClose={() => setIsOpen(false)}
          winners={props.winners}
        />
      )}
    </Box>
  );
};
